import React, { useContext } from 'react'
import { useForm } from 'react-hook-form'
import { AppContext } from '@state/context'
import { closePanel } from '@state/actions/panelsActions'
import Button from '@components/Button'
import ButtonWithLoader from '@components/ButtonWithLoader'
import Heading from '@components/Heading'
import Divider from '@components/Divider'
import Form from '@components/Form'
import {
  PANELS,
  DIVIDER_SIZE,
  BUTTON_SIZE,
  HEADING_TYPE,
  BUTTON_TYPE,
  TEXT_SIZES,
} from '@helpers/constants'
import Text from '@components/Text'
import { useCurrentUser } from '@hooks/useCurrentUser'
import { useSchool } from '@hooks/useSchool'
import Loader from '@components/Loader'
import { useUpsertSchoolGroup } from '@hooks/mutations/useUpsertSchoolGroup'
import { useSchoolGroup } from '@hooks/useSchoolGroup'
import { useDeleteSchoolGroup } from '@hooks/mutations/useDeleteSchoolGroup'
import { useNavigate } from 'react-router-dom'
import { useConfirm } from '@hooks/useConfirm'

const EditSchoolGroupPanel = () => {
  const [state, dispatch] = useContext(AppContext)
  const navigate = useNavigate()
  const { schoolGroup, isLoading: isLoadingGroup } = useSchoolGroup(
    state?.panels?.panelData?.schoolGroupId
  )
  const { currentUser } = useCurrentUser()
  const { school, isLoading } = useSchool(currentUser?.schools[0].id)
  const { post, isPosting } = useUpsertSchoolGroup()
  const { post: postDelete, isPosting: isDeleting } = useDeleteSchoolGroup()
  const { confirm } = useConfirm()

  const {
    handleSubmit,
    errors,
    register,
    watch,
    formState: { isDirty },
  } = useForm()
  const watchName = watch('name')

  const onSubmit = async (data) => {
    const res = await post(
      {
        id: schoolGroup.id,
        name: data.name,
      },
      'Ändringar sparade.'
    )

    if (res.success) dispatch(closePanel(PANELS.EDIT_SCHOOL_GROUP))
  }

  const deleteGroup = async () => {
    const confirmed = await confirm({
      title: schoolGroup.name,
      text: 'Är du säker på att du vill radera denna grupp? Gruppen kommer att försvinna för alla administratörer. Alla böcker som är delade i gruppen kommer att försvinna för eleverna.',
      confirmText: 'Ja, radera grupp',
      cancelText: 'Avbryt',
    })

    if (!confirmed) return

    const res = await postDelete(schoolGroup.id)

    if (res.success) navigate(`/lararrum`)
  }

  return (
    <>
      <div className='flex justify-between'>
        <div className={'flex flex-col'}>
          <Text>Mitt lärarrum - {school?.name ?? ''}</Text>
          <Heading headingLevel={HEADING_TYPE.H2} size={TEXT_SIZES.XLARGE}>
            Redigera grupp
          </Heading>
        </div>
        <div className={'self-start'}>
          <Button
            handleClick={() => {
              dispatch(closePanel(PANELS.EDIT_SCHOOL_GROUP))
            }}
            size={BUTTON_SIZE.NO_PADDING}
            icon={'Close'}
          />
        </div>
      </div>

      {isLoadingGroup || isLoading || (!schoolGroup && <Loader />)}
      {schoolGroup && (
        <>
          <Divider size={DIVIDER_SIZE.BASE} />

          <Form onSubmit={handleSubmit(onSubmit)}>
            <Form.Label htmlFor='name'>Gruppens namn</Form.Label>
            <Form.Input
              type={'text'}
              name='name'
              id='name'
              defaultValue={schoolGroup.name}
              ref={register({ required: true })}
              placeholder=''
            />
            {errors.name && <Form.ErrorMessage>Namn behövs</Form.ErrorMessage>}

            <Divider size={DIVIDER_SIZE.BASE} />

            <ButtonWithLoader
              type={BUTTON_TYPE.PRIMARY}
              isLoading={isPosting}
              enabledText={'Spara ändringar'}
              disabled={isPosting || !isDirty || watchName === ''}
            />
            <span className='ml-4'>
              <ButtonWithLoader
                type={BUTTON_TYPE.ALERT_RED_DARK}
                isLoading={isDeleting}
                enabledText={'Radera grupp'}
                disabled={isPosting || isDeleting}
                handleClick={deleteGroup}
                nativeTypeAttribute={'button'}
              />
            </span>
          </Form>
        </>
      )}
    </>
  )
}

export default EditSchoolGroupPanel
